/*!
 * Abstract Base Class for providers that parse a binding string 🥊  tko.provider.bindingstring@4.0.0-alpha5c
 * (c) The Knockout.js Team - https://tko.io/
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { Parser, parseObjectLiteral } from 'tko.utils.parser';
import { Provider } from 'tko.provider';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = Object.setPrototypeOf ||
    ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
    function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __values(o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

/**
 * BindingStringProvider is an abstract base class parses a binding string.
 *
 * Children must implement `nodeHasBindings` and `getBindingString`.
 */
var BindingStringProvider = /** @class */ (function (_super) {
    __extends(BindingStringProvider, _super);
    function BindingStringProvider() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /** Call bindingHandler.preprocess on each respective binding string.
     *
     * The `preprocess` property of bindingHandler must be a static
     * function (i.e. on the object or constructor).
     */
    BindingStringProvider.prototype.processBinding = function (key, value) {
        var e_1, _a, _b, handlerName, property, handler, bindingsAddedByHandler_2, chainFn, bindingsAddedByHandler_1, bindingsAddedByHandler_1_1, _c, key_1, value_1, e_1_1;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    _b = __read(key.split('.'), 2), handlerName = _b[0], property = _b[1];
                    handler = this.bindingHandlers.get(handlerName);
                    if (!(handler && handler.preprocess)) return [3 /*break*/, 9];
                    bindingsAddedByHandler_2 = [];
                    chainFn = function () {
                        var args = [];
                        for (var _i = 0; _i < arguments.length; _i++) {
                            args[_i] = arguments[_i];
                        }
                        return bindingsAddedByHandler_2.push(args);
                    };
                    value = handler.preprocess(value, key, chainFn);
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 6, 7, 8]);
                    bindingsAddedByHandler_1 = __values(bindingsAddedByHandler_2), bindingsAddedByHandler_1_1 = bindingsAddedByHandler_1.next();
                    _d.label = 2;
                case 2:
                    if (!!bindingsAddedByHandler_1_1.done) return [3 /*break*/, 5];
                    _c = __read(bindingsAddedByHandler_1_1.value, 2), key_1 = _c[0], value_1 = _c[1];
                    return [5 /*yield**/, __values(this.processBinding(key_1, value_1))];
                case 3:
                    _d.sent();
                    _d.label = 4;
                case 4:
                    bindingsAddedByHandler_1_1 = bindingsAddedByHandler_1.next();
                    return [3 /*break*/, 2];
                case 5: return [3 /*break*/, 8];
                case 6:
                    e_1_1 = _d.sent();
                    e_1 = { error: e_1_1 };
                    return [3 /*break*/, 8];
                case 7:
                    try {
                        if (bindingsAddedByHandler_1_1 && !bindingsAddedByHandler_1_1.done && (_a = bindingsAddedByHandler_1["return"])) _a.call(bindingsAddedByHandler_1);
                    }
                    finally { if (e_1) throw e_1.error; }
                    return [7 /*endfinally*/];
                case 8: return [3 /*break*/, 10];
                case 9:
                    if (property) {
                        value = "{" + property + ":" + value + "}";
                    }
                    _d.label = 10;
                case 10: return [4 /*yield*/, "'" + handlerName + "':" + value];
                case 11:
                    _d.sent();
                    return [2 /*return*/];
            }
        });
    };
    BindingStringProvider.prototype.generateBindingString = function (bindingStringOrObjects) {
        var e_2, _a, bindingObjectsArray, bindingObjectsArray_1, bindingObjectsArray_1_1, _b, key, unknown, value, e_2_1;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    bindingObjectsArray = typeof bindingStringOrObjects === 'string'
                        ? parseObjectLiteral(bindingStringOrObjects) : bindingStringOrObjects;
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 6, 7, 8]);
                    bindingObjectsArray_1 = __values(bindingObjectsArray), bindingObjectsArray_1_1 = bindingObjectsArray_1.next();
                    _c.label = 2;
                case 2:
                    if (!!bindingObjectsArray_1_1.done) return [3 /*break*/, 5];
                    _b = bindingObjectsArray_1_1.value, key = _b.key, unknown = _b.unknown, value = _b.value;
                    return [5 /*yield**/, __values(this.processBinding(key || unknown, value))];
                case 3:
                    _c.sent();
                    _c.label = 4;
                case 4:
                    bindingObjectsArray_1_1 = bindingObjectsArray_1.next();
                    return [3 /*break*/, 2];
                case 5: return [3 /*break*/, 8];
                case 6:
                    e_2_1 = _c.sent();
                    e_2 = { error: e_2_1 };
                    return [3 /*break*/, 8];
                case 7:
                    try {
                        if (bindingObjectsArray_1_1 && !bindingObjectsArray_1_1.done && (_a = bindingObjectsArray_1["return"])) _a.call(bindingObjectsArray_1);
                    }
                    finally { if (e_2) throw e_2.error; }
                    return [7 /*endfinally*/];
                case 8: return [2 /*return*/];
            }
        });
    };
    BindingStringProvider.prototype.preProcessBindings = function (bindingStringOrObjects) {
        return Array.from(this.generateBindingString(bindingStringOrObjects))
            .join(',');
    };
    BindingStringProvider.prototype.getBindingAccessors = function (node, context) {
        var bindingString = node && this.getBindingString(node);
        if (!bindingString) {
            return;
        }
        var processed = this.preProcessBindings(bindingString);
        return new Parser().parse(processed, context, this.globals, node);
    };
    BindingStringProvider.prototype.getBindingString = function () { throw new Error('Overload getBindingString.'); };
    return BindingStringProvider;
}(Provider));

export { BindingStringProvider };
//# sourceMappingURL=tko.provider.bindingstring.js.map
